import { useMemo} from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFODerivativeChart = ({ 
  gridView, 
  showSpikeLines, 
  events,
  showLegend
}) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = currentStage?.coldboreData;
  const stageMetrics = currentStage?.metrics;

  const darkMode = user?.value?.darkMode;
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (!stageData || stageData?.length === 0) {
      return [];
    };

    function getChartData() {
      try {
        const X = [];
        const YPressure = [];
        const YPressurePrime = [];
        const YPressurePrimePrime = [];
        const YRate = [];
        const YRatePrime = [];
        const YRNP = [];
        const XSpikes = [];
        const YPressureSpikes = [];
        const YRateSpikes = [];
        const YRNPSpikes = [];


        stageData.forEach(d => {
          X.push(new Date(d.timestamp));
          YPressure.push((d.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YPressurePrime.push(d.pressurePrime || 0);
          YPressurePrimePrime.push(d.pressurePrimePrime || 0);
          YRate.push((d.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1));
          YRatePrime.push(d.ratePrime || 0);
          YRNP.push((d.rnp || null) * (units === 'field' ? (rtfoConversionFactors.rnp / 20) : 1)); // divide field by 20
        });
  
        (currentStage?.events?.perfClosure || currentStage?.spikes)?.forEach(d => {
          XSpikes.push(new Date(d.timestamp));
          YPressureSpikes.push((d.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YRateSpikes.push((d.rate || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YRNPSpikes.push((d.rnp || 0) * (units === 'field' ? (rtfoConversionFactors.rnp / 20) : 1));
        });

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[2]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        let pressurePrimeTrace = {
          x: X,
          y: YPressurePrime,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y3',
          name: 'Pressure\'',
          line: {
            width: 2,
            color: chartColors[7],
            dash: 'dot'
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true,
          hoverformat: '.2f'
        };
        let pressurePrimePrimeTrace = {
          x: X,
          y: YPressurePrimePrime,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y3',
          name: 'Pressure\'\'',
          line: {
            width: 2,
            color: chartColors[5],
            dash: 'dashdot'
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[3]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };
        let ratePrimeTrace = {
          x: X,
          y: YRatePrime,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Rate\'',
          line: {
            width: 2,
            color: chartColors[4],
            dash: 'dash'
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };
        let rnpTrace = { // fracfeed
          x: X,
          y: YRNP,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Fracfeed',
          line: {
            width: 2,
            color: chartColors[1]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };
        let pressureSpikesTrace = {
          x: XSpikes,
          y: YPressureSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y1',
          xaxis: 'x1',
          name: 'Rate Drop',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };
        let rateSpikesTrace = {
          x: XSpikes,
          y: YRateSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate Drop',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          showlegend: false,
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };
        let rnpSpikesTrace = {
          x: XSpikes,
          y: YRNPSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Rate Drop',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          showlegend: false,
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f'
        };

        return [ 
          pressurePrimeTrace, 
          pressurePrimePrimeTrace,
          ratePrimeTrace,
          rnpTrace,
          treatingPressureTrace,
          rateTrace,
          pressureSpikesTrace,
          rateSpikesTrace,
          rnpSpikesTrace
        ];
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };

    return getChartData(stageData)
  }, [stageData, currentStage?.events?.perfClosure, currentStage?.spikes, showSpikeLines, units]);


  const layout = {
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: units === 'field' ? [0, 15000] : [0, 100],
      tick0: 0,
      dtick: units === 'field' ? 3000 : 20,
      rangemode: 'nonnegative',
      side: 'left',
      hoverformat: '.2f'
    },
    yaxis2: { // Slurry rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12
        },
      },
      range: units === 'field' ? [0, 150] : [0, 25],
      rangemode: 'nonnegative',
      tick0: 0,
      dtick: units === 'field' ? 30 : 5,
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0,
      hoverformat: '.2f'
    },
    yaxis3: { // Pressure and rate derivative y axis
      linecolor: chartColors[7],
      showgrid: false,
      color: chartColors[7],
      title: {
        text: 'Pressure and Rate Derivative',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [-15, 15],
      side: 'right',
      overlaying: 'y',
      hoverformat: '.2f'
    },
    yaxis4: { // Rate normalized pressure y axis
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: 'Fracfeed',
        font: {
          size: 12
        }
      },
      range: [0, 10],
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: 1,
      hoverformat: '.2f'
    },
    xaxis: { // Date x axis
      type: 'date',
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: "Pressure Derivative",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      stageMetrics?.preSandTimestamp && { // pre sand line annotation
        x: new Date(stageMetrics?.preSandTimestamp),
        y: 1.01,
        xref: 'x1',
        yref: 'paper',
        text: 'Pre Sand',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      },
      stageMetrics?.breakdownTimestamp && { // breakdown line annotation
        x: new Date(stageMetrics?.breakdownTimestamp),
        y: 1.01,
        xref: 'x1',
        yref: 'paper',
        text: 'Breakdown',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      }
    ],
    shapes: [
      stageMetrics?.preSandTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stageMetrics?.preSandTimestamp),
        y0: 0,
        x1: new Date(stageMetrics?.preSandTimestamp),
        y1: 1,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 1,
          dash: 'dot'
        }
      },
      stageMetrics?.breakdownTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stageMetrics?.breakdownTimestamp),
        y0: 0,
        x1: new Date(stageMetrics?.breakdownTimestamp),
        y1: 1,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 1,
          dash: 'dot'
        }
      }
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  if ((events?.plugSlip || currentStage?.plugSlips)?.length > 0) {
    const newPlugSlipsLines = (events?.plugSlip || currentStage?.plugSlips)?.map((plugSlip, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(plugSlip?.timestamp),
        y0: 0,
        x1: new Date(plugSlip?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newPlugSlipsAnnotations = (events?.plugSlip || currentStage?.plugSlips).map((plugSlip, i) => {
      return {
        x: new Date(plugSlip.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Plug Slip',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newPlugSlipsLines);
    layout.annotations = layout.annotations.concat(newPlugSlipsAnnotations);
  };

  if (events?.sandoff?.length > 0) {
    const newSandoffLines = events?.sandoff.map((s, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(s?.timestamp),
        y0: 0,
        x1: new Date(s?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newSandoffAnnotations = events?.sandoff.map((s, i) => {
      return {
        x: new Date(s.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Sandoff',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newSandoffLines);
    layout.annotations = layout.annotations.concat(newSandoffAnnotations);
  };

  if (events?.isolationIssue?.length > 0) {
    const newIsolationIssueLines = events?.isolationIssue.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newIsolationIssueAnnotations = events?.isolationIssue.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Isolation Issue',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newIsolationIssueLines);
    layout.annotations = layout.annotations.concat(newIsolationIssueAnnotations);
  };

  if (events?.equipmentIssue?.length > 0) {
    const newEquipmentIssueLines = events?.equipmentIssue.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newEquipmentIssueAnnotations = events?.equipmentIssue.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Equipment Issue',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newEquipmentIssueLines);
    layout.annotations = layout.annotations.concat(newEquipmentIssueAnnotations);
  };

  if (events?.lostSuction?.length > 0) {
    const newLostSuctionLines = events?.lostSuction.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newLostSuctionAnnotations = events?.lostSuction.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Lost Suction',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLostSuctionLines);
    layout.annotations = layout.annotations.concat(newLostSuctionAnnotations);
  };

  if (events?.ballSeating?.length > 0) {
    const newLines = events?.ballSeating?.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newAnnotations = events?.ballSeating.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Ball Seating',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLines);
    layout.annotations = layout.annotations.concat(newAnnotations);
  };

  if (events?.other?.length > 0) {
    const newOtherEventsLines = events?.other?.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newOtherEventsAnnotations = events?.other?.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Other',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newOtherEventsLines);
    layout.annotations = layout.annotations.concat(newOtherEventsAnnotations);
  };
  
  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

};
